import { ErrorMessage, Field, Formik } from "formik";
import React, { useState, useEffect } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { LoadingSpinner } from "../LoadingSpinner";
import { IProduct } from "../../customTypings/Product";
import { productCategoryService, productService, userService } from "../../services";
import { showToastSuccess, showToastError } from "../../helpers";
import Select from "react-select";
import { ReactSelectBootstrapStyle } from "../../styles/ReactSelectBootstrapStyle";
import { useAtom } from "@dbeining/react-atom";
import { userAtom } from "../../atoms/userAtom";

type Props = {
    show: boolean;
    currentProduct?: IProduct;
    onClose: (updated: boolean) => void;
};

const ProductEditModel: React.FC<Props> = ({ show, currentProduct, onClose }) => {
    const [alertVariant, setAlertVariant] = useState("danger");
    const [allProductCategories, setAllProductCategories] = useState<any[]>();
    const [allUsers, setAllUsers] = useState<any[]>();
    const currentUser = useAtom(userAtom);

    useEffect(() => {
        Promise.all([productCategoryService.getAllListItems(), userService.getAllListItems()]).then((response) => {
            setAllProductCategories(response[0]);
            setAllUsers(response[1]);
        });
    }, []);

    const handleClose = () => onClose(false);

    return (
        <Modal centered show={show} keyboard={false} onHide={handleClose}>
            <Formik
                initialValues={{
                    name: currentProduct?.name || "",
                    creationDate: currentProduct?.creationDate || new Date(),
                    price: currentProduct?.price || 0,
                    addedBy: currentProduct?.addedBy || currentUser?.identity.id || "",
                    productCategoryId: currentProduct?.productCategoryId || "",
                }}
                enableReinitialize
                onSubmit={(values, { setStatus, setSubmitting, setFieldError }) => {
                    setStatus();
                    setSubmitting(false);

                    const updatedProduct: IProduct = {
                        id: currentProduct?.id,
                        name: values.name,
                        creationDate: values.creationDate,
                        price: values.price,
                        addedBy: values.addedBy,
                        productCategoryId: values.productCategoryId,
                    };

                    productService.edit(updatedProduct).then(
                        (response) => {
                            setSubmitting(false);
                            if (response.status !== "Failure") {
                                setAlertVariant("success");
                                showToastSuccess("Product updated successfully!");
                                setTimeout(() => onClose(true), 1000);
                            } else {
                                setAlertVariant("danger");
                                showToastError(response.message);
                            }
                        },
                        (error: any) => {
                            setAlertVariant("danger");
                            if (error.status === 400 && error.errors) {
                                Object.keys(error.errors).forEach((key) => {
                                    const messages = error.errors[key];
                                    if (Array.isArray(messages)) {
                                        messages.forEach((message) => showToastError(message));
                                    }
                                });
                            } else {
                                showToastError("An unexpected error occurred!");
                            }
                            setSubmitting(false);
                        }
                    );
                }}
            >
                {({ errors, status, touched, isSubmitting, handleSubmit, values, setFieldValue }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Modal.Header closeButton>
                            <Modal.Title>Edit Product</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form.Group as={Row} className="mb-3" controlId="name">
                                <Form.Label column md={3}>
                                    Name <span style={{ color: 'red' }}>*</span>
                                </Form.Label>
                                <Col md={9}>
                                    <Field name="name" type="text" className={"form-control" + (errors.name && touched.name ? " is-invalid" : "")} />
                                    <ErrorMessage name="name" component="div" className="invalid-feedback" />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3" controlId="productCategoryId">
                                <Form.Label column sm={3}>
                                    Product Category <span style={{ color: 'red' }}>*</span>
                                </Form.Label>
                                <Col sm={9}>
                                    <Select
                                        styles={ReactSelectBootstrapStyle}
                                        className="stateManagedSelect"
                                        onChange={(value) => setFieldValue("productCategoryId", value == null ? "" : value.value)}
                                        defaultValue={allProductCategories?.find((cat) => cat.value === currentProduct?.productCategoryId)}
                                        isClearable
                                        options={allProductCategories}
                                    />
                                    <ErrorMessage name="productCategoryId" component="div" className="invalid-feedback" />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3" controlId="price">
                                <Form.Label column md={3}>
                                    Price <span style={{ color: 'red' }}>*</span>
                                </Form.Label>
                                <Col md={9}>
                                    <Field name="price" type="number" className={"form-control" + (errors.price && touched.price ? " is-invalid" : "")} />
                                    <ErrorMessage name="price" component="div" className="invalid-feedback" />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3" controlId="creationDate">
                                <Form.Label column sm={3}>
                                    Creation Date <span style={{ color: 'red' }}>*</span>
                                </Form.Label>
                                <Col sm={9}>
                                    <Field name="creationDate" type="date" className={"form-control" + (errors.creationDate && touched.creationDate ? " is-invalid" : "")} />
                                    <ErrorMessage name="creationDate" component="div" className="invalid-feedback" />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3" controlId="addedBy">
                                <Form.Label column sm={3}>
                                    Added By <span style={{ color: 'red' }}>*</span>
                                </Form.Label>
                                <Col sm={9}>
                                    <Select
                                        styles={ReactSelectBootstrapStyle}
                                        className="stateManagedSelect"
                                        onChange={(value) => setFieldValue("addedBy", value == null ? "" : value.value)}
                                        defaultValue={allUsers?.find((user) => user.value === currentProduct?.addedBy)}
                                        isClearable
                                        options={allUsers}
                                    />
                                    <ErrorMessage name="addedBy" component="div" className="invalid-feedback" />
                                </Col>
                            </Form.Group>
                            {status && (
                                <Alert variant={alertVariant} className="mt-3">
                                    {status}
                                </Alert>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="primary" disabled={isSubmitting} type="submit" className="me-2">
                                {isSubmitting ? <LoadingSpinner text="Updating Product..." /> : "Update Product"}
                            </Button>
                            <Button variant="secondary" onClick={handleClose}>
                                Cancel
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export { ProductEditModel };
